import type { Page, Question } from '../../types';
import { cpfMetrics } from './metrics';

const metricGroups = {
  P: 'Payroll',
  P2P: 'Purchase to pay',
  Q: 'End-user satisfaction',
  JML: 'Government transfers',
  R2R: 'Record to report',
  E: 'Expenses',
  R: 'Recruitment',
  SR: 'Service requests',
  RS: 'Call handling',
  SA: 'Service access',
  SM: 'System availability',
  SSP: 'Provider performance',
};

export const metricQuestions: Question[] = Object.entries(metricGroups).map(([group, title]) => ({
  questionType: 'dynamicTable',
  name: `cpf-${group.toLowerCase()}-metrics`,
  title,
  hideOptionalLabel: true,
  hint: 'Enter each monthly figure, or explain missing figures in Notes. Open metric guidance for the expected format.',
  rules: { required: true },
  dynamicTable: {
    rowHeader: 'Metric',
    rowHintsAsDialog: true,
    strict: true,
    compact: true,
    stickyHeaders: true,
    stickyRowHeader: true,
    highlightActive: true,
    height: 640,
    showExcelToolbar: true,
    confirmExcelImport: true,
    columns: [
      ...['Month 1', 'Month 2', 'Month 3'].map((label, index) => ({
        key: `month${index + 1}`,
        label,
        hint: 'Months in the selected quarter, in calendar order. Enter 0 for zero; leave unavailable figures blank and explain in Notes.',
        inputType: 'number' as const,
        required: false,
        requiredUnlessColumn: 'notes',
        strictNumeric: true,
      })),
      {
        key: 'notes',
        label: 'Notes',
        inputType: 'text',
        minWidth: 300,
        required: false,
        hint: 'Identify each missing month and explain why data is unavailable. You can also add context for the figures supplied.',
      },
    ],
    rows: cpfMetrics
      .filter((metric) => metric.group === group)
      .map((metric) => ({
        key: metric.code.toLowerCase(),
        label: `${metric.code} ${metric.label}`,
        allowDecimal: metric.allowDecimal,
        allowNegativeNumbers: metric.allowNegativeNumbers,
        hint: `${metric.label}. ${metric.allowDecimal ? 'Decimals are allowed.' : 'Enter whole numbers.'} ${metric.allowNegativeNumbers ? 'A signed difference is allowed.' : 'Enter non-negative figures.'} Do not enter currency symbols, commas or percentages. ${group === 'R' ? 'Enter the figure from your recruitment source; automatic prepopulation is not yet connected.' : ''}`,
      })),
  },
}));

const monthlyMetrics: Page[] = [
  {
    pageType: 'question',
    name: 'monthly-metrics',
    title: 'Monthly metrics',
    limitWidth: false,
    description: `Report three months within your selected quarter.

Month 1 / Month 2 / Month 3: Q1 = April / May / June; Q2 = July / August / September; Q3 = October / November / December; Q4 = January / February / March.

Use the Excel download and upload controls to complete all 74 metrics offline.`,
    questions: metricQuestions,
  },
  {
    pageType: 'question',
    name: 'general-comments',
    title: 'General comments',
    questions: [
      {
        questionType: 'textarea',
        name: 'general-comments',
        title: 'General comments',
        hint: 'Add overarching context, changes in coverage or feedback not tied to a specific metric.',
        textRows: 8,
        rules: { required: false },
      },
    ],
  },
];

export default monthlyMetrics;
